import React, { useEffect, useMemo, useState } from "react";

/**
 * Fence-view watermark overlay.
 *
 * Renders a tile-repeated SVG pattern (multi-line, rotated -30°) carrying
 * the viewer's identity, the room name, current page, and a *live*
 * timestamp that refreshes every minute. Because it's emitted as an SVG
 * `background-image`, the pattern tiles correctly across documents of
 * ANY length — including the new continuously-scrolled multi-page PDFs
 * where the document surface can exceed 10 000 px in height.
 *
 * Responsibilities while mounted:
 *   1. Paint the watermark across the full document surface.
 *   2. Suppress save / print / dev-tools / right-click / text-selection
 *      shortcuts.
 *
 * `enabled=false` short-circuits BOTH responsibilities — pass `false`
 * for admins so they see the original document with full browser controls
 * (the fence-view UX is for external viewers, not the room owners).
 */
export default function FenceWatermark({ user, extra = "", enabled = true }) {
  // Tick the timestamp every minute so a long reading session shows
  // when each page was actually on-screen, not when the viewer opened.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, [enabled]);

  // Keyboard / mouse hostile-action blockers.
  useEffect(() => {
    if (!enabled) return undefined;
    const block = (e) => { e.preventDefault(); return false; };
    const blockKeys = (e) => {
      const k = e.key?.toLowerCase();
      if (e.ctrlKey || e.metaKey) {
        if (["p", "s", "u"].includes(k)) { e.preventDefault(); return false; }
        if (e.shiftKey && ["i", "j", "c"].includes(k)) { e.preventDefault(); return false; }
      }
      if (k === "f12") { e.preventDefault(); return false; }
    };
    document.addEventListener("contextmenu", block);
    document.addEventListener("keydown", blockKeys);
    document.body.classList.add("no-select");
    return () => {
      document.removeEventListener("contextmenu", block);
      document.removeEventListener("keydown", blockKeys);
      document.body.classList.remove("no-select");
    };
  }, [enabled]);

  const text = useMemo(() => {
    if (!enabled) return "";
    const identity = user?.name
      ? `${user.name} · ${user.email}`
      : user?.email || "GUEST";
    // Compact format for the in-reader watermark: identity + room (no
    // page number — the toolbar shows it; no "SOVEREIGN VDR" prefix —
    // the logo carries that) + ISO-ish timestamp at hour precision so
    // it's both stable across short reading sessions and short enough
    // to repeat several times across a typical document column.
    const stamp = now.toISOString().replace("T", " ").slice(0, 16) + " UTC";
    const room = (extra || "").split(" · ")[0] || "";
    return room ? `${identity} · ${room} · ${stamp}` : `${identity} · ${stamp}`;
  }, [enabled, user, extra, now]);

  // Build a tile-able SVG using a native `<pattern patternTransform>` so
  // the -30° rotation is applied as the SVG tiles the rect (not after
  // placement). The previous implementation rotated a group of <text>
  // nodes *about the tile centre* — which threw ~80% of the strings out
  // of the SVG's clip box, leaving the watermark only barely visible in a
  // narrow diagonal band. Using `<pattern>` keeps every glyph inside the
  // visible rect, producing a dense, evenly-distributed diagonal grid
  // that matches the encrypted-PDF download.
  const tileUrl = useMemo(() => {
    if (!enabled || !text) return null;
    const safe = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    const px_per_char = 10.6;                  // 13px Helvetica + 1px letter-spacing
    const text_w = Math.ceil(text.length * px_per_char);
    // Pattern-unit cell (in unrotated pattern space). One staggered pair
    // of strings per cell — alternating rows offset by half a stride
    // break the perfect grid look without leaving holes.
    const step_x = text_w + 28;                // horizontal stride
    const step_y = 36;                         // vertical stride between rows
    const cell_w = step_x;
    const cell_h = step_y * 2;
    // SVG canvas. Make it generously large so CSS tiling of the bitmap
    // version is rarely visible at typical document sizes. The pattern
    // inside tiles seamlessly within the SVG via SVG's own machinery.
    const tile_w = 1600;
    const tile_h = 1600;
    const textAttrs =
      "font-family='Helvetica, Arial, sans-serif' font-size='13' " +
      "font-weight='400' fill='#141414' fill-opacity='0.20' letter-spacing='1'";
    const svg =
      `<svg xmlns='http://www.w3.org/2000/svg' width='${tile_w}' height='${tile_h}'>` +
      `<defs>` +
      `<pattern id='wm' x='0' y='0' width='${cell_w}' height='${cell_h}' ` +
        `patternUnits='userSpaceOnUse' patternTransform='rotate(-30)'>` +
      `<text x='0' y='${step_y - 10}' ${textAttrs}>${safe}</text>` +
      `<text x='${step_x / 2}' y='${step_y * 2 - 10}' ${textAttrs}>${safe}</text>` +
      `</pattern>` +
      `</defs>` +
      `<rect width='100%' height='100%' fill='url(#wm)'/>` +
      `</svg>`;
    return {
      url: `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`,
      tile_w,
      tile_h,
    };
  }, [enabled, text]);

  if (!enabled) return null;

  return (
    <div
      className="fence-watermark"
      data-testid="fence-watermark"
      aria-hidden="true"
      style={{
        backgroundImage: tileUrl?.url,
        backgroundRepeat: "repeat",
        backgroundSize: `${tileUrl?.tile_w}px ${tileUrl?.tile_h}px`,
      }}
    />
  );
}
